import { Component, Inject, OnInit, Optional } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { WebClient } from '@injectables';
import { formatStr } from '@lib/angular/measurement/format-str';
import { MeasurementTypeToUnit } from '@lib/angular/measurement/measurement-type-to-unit';
import { measurement } from '@lib/measurement';
import { UnitSystem } from '@lib/measurement/types';
import { asFiniteNumber } from '@lib/number';
import { BehaviorSubject, op } from '@lib/rxjs';
import { Client, DataIntervalTag, User } from '@models';
import { FragQueryService } from 'src/injectables/frag-query.service';

interface Input {
	record: DataIntervalTag;
	showTimes?: boolean;
}

@Component({
	selector: 'data-interval-tag-edit-dialog',
	templateUrl: './data-interval-tag-edit-dialog.component.html',
	styleUrls: [
		'./data-interval-tag-edit-dialog.component.scss'
	]
})
export class DataIntervalTagEditDialog implements OnInit {
	public readonly quantityUnitSystem$ = this.fragQuery.QuantityUnitSystem$;
	public Client: Client | undefined = undefined;
	public materialTypes: string[] = [];
	public clients$ = this.webClient.model.client.search$({}).pipe(op.startWith([]));
	public benchHeightOption: { value: number, label: string }[] = [];
	public benchSeamOption: string[] = []
	public restClient: Client[];
	public typeMaterial$: BehaviorSubject<string> = new BehaviorSubject<string>('');
	public readonly selectedClient$ = this.fragQuery.client$;

	static readonly config: MatDialogConfig<Input> = {
		autoFocus: false,
		disableClose: true,
	};
	static readonly output: boolean;

	constructor(
		private readonly webClient: WebClient,
		public readonly user: User,
		private readonly host: MatDialogRef<DataIntervalTagEditDialog, boolean>,
		private readonly fragQuery: FragQueryService,
		@Inject(MAT_DIALOG_DATA) private readonly input: Input,
		@Optional() private readonly measurementTypeToUnit: MeasurementTypeToUnit,
	) {
	}

	public readonly particleSizeUnitSystem$ = this.fragQuery.particleSizeUnitSystem$;
	public showTimes = this.input.showTimes ?? true;

	public record = this.input.record;
	public begin = this.record.beginDateTime;
	public end = this.record.endDateTime;
	public benchHeight = this.record.benchHeightSize
	public benchSeam = this.record.benchSeam
	public truckLoadCount = this.record.truckLoadCount;
	public materialType: string = this.record.materialType ?? "";
	public truckMaterialDensity = this.record.truckMaterialDensity;
	public comments = this.record.comments;
	public unitStr = '';
	private readonly unitSystem$ = new BehaviorSubject<UnitSystem>('imperialUnit');

	ngOnInit() {
		this.particleSizeUnitSystem$.subscribe((unit) => {
			this.unitSystem$.next(unit);
		});

		let typeToUnit = this.measurementTypeToUnit.getUnitsForSystem(this.unitSystem$.value);
		this.unitStr = measurement.abbreviation(typeToUnit["distance"]);

		this.selectedClient$.subscribe((Client) => {
			this.Client = Client;
			let materialTypeArray: { text: string, color: string }[] = [];

			if (this.Client && typeof this.Client.materialType === 'string' && this.Client.materialType.trim().length > 0) {
				try {
					materialTypeArray = JSON.parse(Client.materialType);
				} catch (e) {
					console.error("Invalid materialType JSON:", e);
				}
			}

			if (materialTypeArray && Array.isArray(materialTypeArray)) {
				this.materialTypes = materialTypeArray.map((type) => type.text);
			}

			if (this.record.materialType) {
				this.typeMaterial$.next(this.record.materialType);
			}

			if (this.Client.benchHeight || this.Client.benchHeight.trim() !== "") {
				const benchHeightArr = this.Client.benchHeight.split(",").map(num => {
					const parsedNum = asFiniteNumber(num);
					if (isNaN(parsedNum)) {
						return null;
					}
					return parsedNum;
				}).filter(num => num !== null);

				if (benchHeightArr.length > 0) {
					const converteHeighdVal = formatStr(benchHeightArr, "distance", typeToUnit, 0, '${v}', false);
					const convertedArr = converteHeighdVal.split(",");
					this.benchHeightOption = convertedArr.map((val, i) => ({
						value: benchHeightArr[i],
						label: val,
					}));
				}
			}

			if (this.Client.benchSeam || this.Client.benchSeam.trim() !== "") {
				this.benchSeamOption = this.Client.benchSeam.split(",").map(seam => seam)
					.filter(seam => seam !== null)
			}
		});

		this.fragQuery.device$.subscribe(device => {
			if (!this.record.truckMaterialDensity || this.record.truckMaterialDensity === 0) {
				try {
					const defaultConfigs = JSON.parse(device.defaultConfigs || '{}');
					if (defaultConfigs.truckMaterialDensity) {
						this.truckMaterialDensity = defaultConfigs.truckMaterialDensity;
					}
				} catch (e) {
					console.warn('Invalid defaultConfig JSON:', e);
				}
			}
		});
	}

	public valid() {
		if (this.showTimes && (!this.begin || (this.end && new Date(this.begin) >= new Date(this.end)))) {
			return false;
		}

		return true;
	}

	public cancel() {
		this.host.close(false);
	}

	public save() {
		this.record.beginDateTime = this.begin;
		this.record.endDateTime = this.end;
		this.record.benchHeightSize = this.benchHeight;
		this.record.benchSeam = this.benchSeam;
		this.record.truckLoadCount = this.truckLoadCount;
		this.record.comments = this.comments;
		this.record.truckMaterialDensity = this.truckMaterialDensity
		const materialTypeValue = this.typeMaterial$?.value;
		if (this.materialTypes?.length > 0) {
			this.record.materialType = materialTypeValue || this.materialType;
		} else {
			this.record.materialType = this.materialType;
		}
		this.host.close(true);
	}
}
